/**
 * UploadedFilesList — file rows with status chips, char counts,
 * vision badges, per-file warnings, and (in editable mode) Trash +
 * Retry affordances.
 *
 * Lifted out of Stage1.tsx as part of the Stage 0 / Stage 1 split.
 * The component owns its own polling so callers don't have to
 * coordinate two sources of truth — Stage 0 (no other consumers)
 * and Stage 1 (PageIndex badge / completion toast / figures-reload
 * token, all driven by the same docs list) both subscribe via the
 * onDocumentsChange callback.
 *
 * Mode prop:
 *   - 'editable'  → Trash + Retry buttons render; clicking either
 *                   calls the matching delete / retry endpoint.
 *   - 'readonly'  → no Trash, no Retry, no delete-confirm state.
 *                   Used in Stage 1's left panel after the upload
 *                   panel was moved to Stage 0.
 *
 * refreshKey: bumping this from the parent forces an immediate
 *             refetch outside the polling cadence — used by
 *             UploadZone's onUploaded so a freshly-uploaded file
 *             surfaces without waiting up to 5 s for the next tick.
 */
import React, { useCallback, useEffect, useRef, useState } from 'react';
import {
  Loader2, AlertTriangle, CheckCircle, FileText, File, Trash2, Circle,
  ChevronDown,
  FileSpreadsheet, FileCode, Mail, FileImage,
} from 'lucide-react';

import * as api from '@/api/client';
import type { MatterDocument, JobStatus } from '@/api/client';


import { apiErrorText } from '../../lib/describeApiError';
type DocTypeValue = 'idf' | 'supplementary' | 'prior_art';

const DOC_TYPE_OPTIONS: ReadonlyArray<{
  value: DocTypeValue;
  label: string;
  description: string;
}> = [
  { value: 'idf',           label: 'Primary IDF',   description: 'Anchors Stage 1 analysis' },
  { value: 'supplementary', label: 'Supplementary', description: 'Supporting material' },
  { value: 'prior_art',     label: 'Prior Art',     description: 'Background reference' },
];

function docTypePillClasses(value: DocTypeValue | string): string {
  switch (value) {
    case 'idf':
      return 'bg-blue-50 text-blue-800 border-blue-200';
    case 'prior_art':
      return 'bg-amber-50 text-amber-800 border-amber-200';
    case 'supplementary':
    default:
      return 'bg-zinc-50 text-zinc-700 border-zinc-200';
  }
}

function docTypeLabel(value: DocTypeValue | string): string {
  const o = DOC_TYPE_OPTIONS.find((x) => x.value === value);
  return o ? o.label : 'Unclassified';
}

/**
 * formatCharCount — "4.9k chars" / "234 chars" / null when zero.
 *
 * Matches the file size convention the rest of the upload UI uses:
 * KB rounded to one decimal at the kilo boundary, plain integer
 * below it. The threshold colour (amber > 80k, red > 100k) mirrors
 * the sync indexer's chunking cap so a drafter can see at a glance
 * which docs will be truncated.
 */
function formatCharCount(n: number): string {
  if (n >= 1000) {
    const k = n / 1000;
    return `${k >= 100 ? Math.round(k) : k.toFixed(1)}k chars`;
  }
  return `${n} chars`;
}

function charCountBadgeCls(n: number): string {
  if (n > 100_000) return 'bg-red-50 text-red-700 border-red-200';
  if (n > 80_000)  return 'bg-amber-50 text-amber-700 border-amber-200';
  return 'bg-zinc-50 text-zinc-600 border-zinc-200';
}


export type UploadedFilesMode = 'editable' | 'readonly';


interface UploadedFilesListProps {
  matterId: string;
  mode?: UploadedFilesMode;
  /** Bump to force an out-of-band refetch (e.g. immediately after
   *  UploadZone reports a successful upload). */
  refreshKey?: number;
  /** Called after every successful list fetch with the filtered
   *  (non-archived) docs. Stage 1 uses this to drive the PageIndex
   *  badge, completion toast, and figures-reload token. Best-effort:
   *  exceptions thrown by the callback are swallowed so a buggy
   *  parent can't break the polling. */
  onDocumentsChange?: (docs: MatterDocument[]) => void;
  /** Backend prevents deletes / retries while Stage 1 is frozen.
   *  Hides the affordances even in editable mode so the drafter
   *  doesn't see an action that the server will refuse. */
  isFrozen?: boolean;
  /** Optional join target for legacy idf_documents — used to surface
   *  the per-file char count + extraction warnings on primary IDF
   *  rows (those columns only exist on the legacy table). When
   *  undefined, char count + warnings are simply hidden, which is
   *  the correct shape for supplementary / prior-art uploads. */
  legacyDocsByName?: Map<string, { extraction_warnings?: string[]; extracted_text?: string }>;
  /** (document_id → job_id) map populated by UploadZone when an
   *  async upload returns 202. When a row's document_id is keyed
   *  here AND its status is in-flight, the file row mounts a
   *  JobProgressPanel that polls /api/jobs/{job_id}/status every
   *  5s and renders the worker's per-step progress (queue position,
   *  extracting / indexing / vision steps, complete checkmark).
   *  Sync-path uploads never populate this map, so the panel never
   *  renders — existing 200-path UI is unchanged. */
  jobIdsByDocument?: Record<string, string>;
  /** OPT-1 — optimistic placeholder rows. Stage0 owns the state
   *  (events come from UploadZone, a sibling) and threads it down
   *  here. Each entry renders as a greyed-out row at the top of
   *  the list with a spinner + "Uploading…" badge. Cleared by the
   *  parent on settle. Sync-path uploads also populate this so
   *  the user sees the row immediately rather than waiting for
   *  the POST roundtrip + the next list refetch. */
  pendingFiles?: Array<{ tempId: string; filename: string; size: number }>;
}


/**
 * Step → 4-row checklist position. Map matches the spec's table:
 *   step=extracting → step 2 active
 *   step=figures    → step 2 done, step 3 active
 *   step=indexing   → step 3 active (steps 1+2 done)
 *   step=vision     → step 4 active (steps 1-3 done)
 *   step=complete   → all done
 *   queued / null   → step 1 done (file received), 2-4 pending
 *
 * Returned tuple is (activeIndex, doneThrough) where indices are
 * 0-based against [received, extract, index, vision]. doneThrough
 * is the highest 0-based index that's marked ✅; activeIndex is
 * the index currently in flight (rendered with the spinner +
 * active label). Either may be -1 to mean "none active / nothing
 * past file-received is done".
 */
function resolveStepIndices(step: string | null | undefined): {
  done: number;     // index of the LAST done step (-1 = nothing done yet)
  active: number;   // index of the currently-active step (-1 = none active)
} {
  switch (step) {
    case 'extracting': return { done: 0, active: 1 };
    case 'figures':    return { done: 1, active: 2 };
    case 'indexing':   return { done: 1, active: 2 };
    case 'vision':     return { done: 2, active: 3 };
    case 'complete':   return { done: 3, active: -1 };
    default:           return { done: 0, active: -1 };  // queued / unknown
  }
}


const STEP_LABELS = [
  'File received',
  'Extracting text',
  'Building search index',
  'Vision analysis',
];


interface JobProgressPanelProps {
  jobId: string;
  /** Fires when poll sees status='finished' / 'failed' so the
   *  parent can refetch listMatterDocuments and let the row pick
   *  up its terminal status (indexed / failed) on the next tick.
   *  Best-effort — exceptions don't unwind the poller. */
  onSettled?: () => void;
}


/**
 * JobProgressPanel — per-row step progress for an in-flight RQ
 * upload. Mounted by UploadedFilesList only when a row's
 * document_id is keyed in the jobIdsByDocument map AND the row's
 * status reflects in-flight work.
 *
 * Polls /api/jobs/{job_id}/status every 5 s. Renders:
 *   - active step label + spinner (or queue position when waiting)
 *   - 4-row checklist with ✅ / ⏳ / ○ icons
 *
 * Auto-stops the poller when status='finished' or 'failed'.
 * Cleanup on unmount via the pollRef pattern UploadZone uses.
 */
function JobProgressPanel({ jobId, onSettled }: JobProgressPanelProps) {
  const [job, setJob] = useState<JobStatus | null>(null);
  const [error, setError] = useState<string | null>(null);
  const settledRef = useRef(false);

  useEffect(() => {
    let cancelled = false;
    const tick = async () => {
      try {
        const data = await api.getJobStatus(jobId);
        if (cancelled) return;
        setJob(data);
        if ((data.status === 'finished' || data.status === 'failed') && !settledRef.current) {
          settledRef.current = true;
          if (onSettled) {
            try { onSettled(); } catch { /* tolerate */ }
          }
        }
      } catch (e: any) {
        if (!cancelled) setError(e?.message || 'Status fetch failed');
      }
    };
    tick();
    const id = setInterval(() => {
      if (settledRef.current) return;
      tick();
    }, 5_000);
    return () => { cancelled = true; clearInterval(id); };
  }, [jobId, onSettled]);

  if (error || !job) {
    // Silent during the very first poll — the row's existing
    // status chip already conveys "something is in flight". A
    // permanent failure to fetch (e.g. job_id not found) is
    // surfaced as a small inline error so the drafter knows
    // why the panel isn't moving.
    if (error) {
      return (
        <div className="mt-1.5 text-[11px] text-amber-700 flex items-center gap-1">
          <AlertTriangle size={10} className="text-amber-500" />
          Job status unavailable ({error})
        </div>
      );
    }
    return null;
  }

  const step = job.progress?.step || (job.status === 'queued' ? 'queued' : null);
  const { done, active } = resolveStepIndices(step);
  const queued = (job.queue_position || 0) > 0;
  const queueMsg = queued
    ? `Position ${job.queue_position} in queue · ~${(job.queue_position || 0) * 30}s estimated`
    : null;
  const activeMsg = job.progress?.message
    || (queued ? queueMsg : null)
    || (step ? `${STEP_LABELS[active] || 'Processing'}…` : 'Waiting for worker…');

  return (
    <div
      data-testid={`job-progress-${jobId}`}
      data-step={step || ''}
      className="mt-2 pl-6 space-y-1"
    >
      <div className="text-[11px] text-zinc-700 flex items-center gap-1.5">
        {active === -1 && job.status !== 'finished' ? (
          <Loader2 size={10} className="animate-spin text-[#002FA7]" />
        ) : null}
        <span>{activeMsg}</span>
      </div>
      <ul className="space-y-0.5">
        {STEP_LABELS.map((label, i) => {
          const isDone = i <= done && active !== i;
          const isActive = i === active;
          const cls = isActive
            ? 'text-[#002FA7] font-medium'
            : isDone
              ? 'text-green-700'
              : 'text-zinc-400';
          const Icon = isActive
            ? Loader2
            : isDone
              ? CheckCircle
              : Circle;
          return (
            <li
              key={i}
              data-testid={`job-step-${i}`}
              data-state={isActive ? 'active' : isDone ? 'done' : 'pending'}
              className={`text-[11px] flex items-center gap-1.5 ${cls}`}
            >
              <Icon
                size={10}
                className={isActive ? 'animate-spin' : ''}
              />
              {label}
            </li>
          );
        })}
      </ul>
    </div>
  );
}


/**
 * DocTypePicker — pill-styled dropdown replacement for the raw
 * <select>. Matches the colour conventions used elsewhere in the
 * file (blue for primary IDF, amber for prior art, zinc for
 * supplementary) and surfaces a one-line description per option so
 * a drafter doesn't have to guess what each category means.
 *
 * Behaviour parity with the previous <select>:
 *   - keyboard activation via Enter/Space on the trigger
 *   - disabled state honours both ``disabled`` (in-flight PATCH)
 *     and the matter-level frozen flag passed from the parent
 *   - onChange fires the same handler as before (handleDocTypeChange)
 *
 * Outside-click dismiss via document mousedown, escape-key close,
 * and a 6-pixel offset so the menu visually anchors to the pill.
 */
interface DocTypePickerProps {
  docId: number;
  value: DocTypeValue;
  disabled: boolean;
  isFrozen: boolean;
  onChange: (next: DocTypeValue) => void;
}

function DocTypePicker({
  docId, value, disabled, isFrozen, onChange,
}: DocTypePickerProps) {
  const [open, setOpen] = useState(false);
  const wrapperRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    if (!open) return undefined;
    const onDocMouseDown = (e: MouseEvent) => {
      if (!wrapperRef.current) return;
      if (!wrapperRef.current.contains(e.target as Node)) setOpen(false);
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setOpen(false);
    };
    document.addEventListener('mousedown', onDocMouseDown);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onDocMouseDown);
      document.removeEventListener('keydown', onKey);
    };
  }, [open]);

  const pillCls = docTypePillClasses(value);
  const title = isFrozen
    ? 'Stage 1 is frozen — unfreeze before re-classifying'
    : 'Re-classify this document';

  return (
    <div ref={wrapperRef} className="relative flex-shrink-0">
      <button
        type="button"
        data-testid={`doc-type-select-${docId}`}
        data-doc-type={value}
        aria-haspopup="listbox"
        aria-expanded={open}
        disabled={disabled}
        title={title}
        onClick={() => setOpen((o) => !o)}
        className={
          'inline-flex items-center gap-1 h-6 px-2 text-[11px] '
          + 'font-medium border rounded-full disabled:opacity-50 '
          + `cursor-pointer transition-colors ${pillCls}`
        }
      >
        <span>{docTypeLabel(value)}</span>
        <ChevronDown size={11} className="opacity-70" />
      </button>
      {open && (
        <div
          role="listbox"
          data-testid={`doc-type-menu-${docId}`}
          className={
            'absolute right-0 top-[calc(100%+6px)] z-20 '
            + 'min-w-[14rem] bg-white border border-zinc-200 '
            + 'rounded-md shadow-lg overflow-hidden'
          }
        >
          {DOC_TYPE_OPTIONS.map((opt) => {
            const isCurrent = opt.value === value;
            return (
              <button
                key={opt.value}
                type="button"
                role="option"
                aria-selected={isCurrent}
                data-testid={`doc-type-option-${docId}-${opt.value}`}
                onClick={() => {
                  setOpen(false);
                  if (!isCurrent) onChange(opt.value);
                }}
                className={
                  'block w-full text-left px-3 py-2 text-xs '
                  + 'border-b border-zinc-100 last:border-0 '
                  + `hover:bg-zinc-50 ${isCurrent ? 'bg-zinc-50' : ''}`
                }
              >
                <div className="flex items-center gap-2">
                  <span className={
                    'inline-block w-1.5 h-1.5 rounded-full '
                    + (opt.value === 'idf' ? 'bg-blue-500'
                      : opt.value === 'prior_art' ? 'bg-amber-500'
                      : 'bg-zinc-400')
                  } />
                  <span className="font-medium text-zinc-800">
                    {opt.label}
                  </span>
                  {isCurrent && (
                    <span className="ml-auto text-[10px] text-zinc-400">
                      current
                    </span>
                  )}
                </div>
                <p className="mt-0.5 text-[11px] text-zinc-500 leading-snug">
                  {opt.description}
                </p>
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
}


export default function UploadedFilesList({
  matterId,
  mode = 'editable',
  refreshKey,
  onDocumentsChange,
  isFrozen,
  legacyDocsByName,
  jobIdsByDocument,
  pendingFiles,
}: UploadedFilesListProps) {
  const [docs, setDocs] = useState<MatterDocument[]>([]);
  const [confirmDeleteDocId, setConfirmDeleteDocId] = useState<number | null>(null);
  const [deletingDocId, setDeletingDocId] = useState<number | null>(null);
  const [retryingId, setRetryingId] = useState<number | null>(null);
  // Tier-1 multi-doc fix: per-row doc_type re-classification. While
  // a row is updating, the dropdown disables itself; on success the
  // list refetches so the new pill / promoted-primary state reflects
  // the server view (PATCH archives the previous primary inside the
  // same transaction, so the refetch is the only correct source of
  // truth post-mutation).
  const [updatingTypeId, setUpdatingTypeId] = useState<number | null>(null);
  const [error, setError] = useState<string | null>(null);
  // OPT-5 — "Updated Xs ago" label. Updated on every successful
  // fetchDocuments() call; rendered as a tiny ticking counter
  // beneath the file list so the drafter knows the list isn't
  // stale.
  const [lastRefresh, setLastRefresh] = useState<Date>(() => new Date());
  const [secondsAgo, setSecondsAgo] = useState(0);

  const editable = mode === 'editable';

  const fetchDocuments = useCallback(async (): Promise<MatterDocument[]> => {
    if (!matterId) return [];
    try {
      const data = await api.listMatterDocuments(matterId);
      const fresh = (data.documents || []).filter((d) => d.status !== 'archived');
      setDocs(fresh);
      setLastRefresh(new Date());
      if (onDocumentsChange) {
        try { onDocumentsChange(fresh); } catch { /* parent-callback errors are best-effort */ }
      }
      return fresh;
    } catch {
      // Polling is advisory — a transient 5xx doesn't break the flow;
      // the next tick recovers.
      return [];
    }
  }, [matterId, onDocumentsChange]);

  // OPT-5 — tick "Updated Xs ago" every second. Cheap; cleared
  // on unmount so an unmounted list doesn't keep timers alive.
  useEffect(() => {
    setSecondsAgo(0);
    const t = setInterval(() => {
      setSecondsAgo(Math.floor(
        (Date.now() - lastRefresh.getTime()) / 1000,
      ));
    }, 1000);
    return () => clearInterval(t);
  }, [lastRefresh]);

  // Initial + 5s polling. Cadence matches the previous inline impl
  // in Stage 1: poll only while at least one row is in flight, then
  // back off so settled matters aren't spamming the API.
  useEffect(() => {
    if (!matterId) return;
    let cancelled = false;
    const tick = async () => {
      const fresh = await fetchDocuments();
      if (cancelled) return;
      void fresh;
    };
    tick();
    const id = setInterval(() => {
      const inFlight = docs.filter(
        (d) => d.status !== 'indexed' && d.status !== 'failed',
      ).length;
      if (inFlight > 0) tick();
    }, 5000);
    return () => { cancelled = true; clearInterval(id); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [matterId, docs.length, docs.map(d => d.status).join(',')]);

  // Out-of-band refetch trigger — UploadZone fires onUploaded after
  // each successful file POST and the parent bumps refreshKey here.
  useEffect(() => {
    if (refreshKey === undefined) return;
    fetchDocuments();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [refreshKey]);

  const handleRetry = async (docId: number) => {
    setRetryingId(docId);
    setError(null);
    try {
      await api.retryMatterDocument(matterId, docId);
      await fetchDocuments();
    } catch (e: any) {
      setError(apiErrorText(e, 'Retry failed'));
    } finally {
      setRetryingId(null);
    }
  };

  const handleDocTypeChange = async (
    doc: MatterDocument,
    newType: 'idf' | 'supplementary' | 'prior_art',
  ) => {
    if (newType === 'idf') {
      const ok = window.confirm(
        'Make this the Primary IDF? The current primary IDF '
        + 'will become Supplementary — it stays in your document '
        + 'list and context. Stage 1 results may need to be '
        + 're-run against the new primary.',
      );
      if (!ok) return;
    }
    setUpdatingTypeId(doc.id);
    setError(null);
    try {
      const result = await api.updateMatterDocument(
        matterId, doc.id, { doc_type: newType },
      );
      await fetchDocuments();
      if (result.warning) {
        // Demoting the primary → no primary remains. Surface the
        // server's warning inline so the drafter is nudged to
        // designate a new primary rather than silently leaving
        // Stage 1 with no anchor.
        setError(result.warning);
      }
    } catch (e: any) {
      const detail = e?.response?.data?.detail;
      setError(
        (typeof detail === 'string' && detail)
        || detail?.message
        || 'Could not update document type.',
      );
    } finally {
      setUpdatingTypeId(null);
    }
  };

  const handleDelete = async (docId: number) => {
    if (isFrozen) return;
    if (confirmDeleteDocId !== docId) {
      setConfirmDeleteDocId(docId);
      return;
    }
    setDeletingDocId(docId);
    setError(null);
    try {
      await api.deleteMatterDocument(matterId, docId);
      // Optimistic local removal so the row vanishes instantly;
      // the next poll tick will catch any stragglers.
      setDocs((prev) => prev.filter((d) => d.id !== docId));
    } catch (e: any) {
      setError(apiErrorText(e, 'Failed to delete document'));
    }
    setDeletingDocId(null);
    setConfirmDeleteDocId(null);
  };

  // OPT-1 — keep the panel mounted when there's at least one
  // pending placeholder so the optimistic row is visible from the
  // very first drop. Without pending entries the panel still
  // hides on an empty matter (preserves the previous "no rows
  // == no chrome" behaviour).
  if (docs.length === 0 && (!pendingFiles || pendingFiles.length === 0)) {
    return null;
  }

  const indexedCount = docs.filter((d) => d.status === 'indexed').length;
  const failedCount = docs.filter((d) => d.status === 'failed').length;
  const inFlightCount = docs.length - indexedCount - failedCount;

  return (
    <div
      className="border border-zinc-200 rounded-md bg-white"
      data-testid="uploaded-files"
    >
      <div className="flex items-center justify-between px-4 pt-4 pb-2 gap-2">
        <span className="text-xs font-medium tracking-wider uppercase text-zinc-500">
          Uploaded Files
        </span>
        <div
          data-testid="uploaded-files-summary"
          className="text-xs text-zinc-500 flex items-center gap-1"
        >
          <span>
            <strong className="text-zinc-700">{docs.length}</strong>{' '}
            file{docs.length === 1 ? '' : 's'}
          </span>
          {indexedCount > 0 && (
            <span className="text-green-700">· {indexedCount} indexed</span>
          )}
          {inFlightCount > 0 && (
            <span className="text-amber-700">· {inFlightCount} processing</span>
          )}
          {failedCount > 0 && (
            <span className="text-red-700">· {failedCount} failed</span>
          )}
        </div>
      </div>

      {error && (
        <div className="mx-4 mb-2 p-2 bg-red-50 border border-red-200 rounded-md text-xs text-red-800 flex items-center gap-1.5">
          <AlertTriangle size={12} /> {error}
        </div>
      )}

      <div className="divide-y divide-zinc-100 border-t border-zinc-100">
        {/* OPT-1 — optimistic placeholder rows for files the
            drafter just dropped. Greyed name, spinner + amber
            "Uploading…" badge, no delete affordance. The parent
            removes the entry as soon as the POST settles
            (success → real row appears via the next list
            refetch; failure → an inline error toast appears at
            the parent). */}
        {(pendingFiles || []).map((p) => (
          <div
            key={p.tempId}
            data-testid={`pending-upload-${p.tempId}`}
            className="px-4 py-3 opacity-70"
          >
            <div className="flex items-center gap-2.5 text-sm">
              <FileText size={16} className="text-zinc-400 flex-shrink-0" />
              <span
                className="text-zinc-500 truncate flex-1 max-w-[24ch]"
                title={p.filename}
              >
                {p.filename}
              </span>
              <span className="text-[11px] text-zinc-400 font-mono flex-shrink-0">
                {p.size > 1024 * 1024
                  ? `${(p.size / (1024 * 1024)).toFixed(1)} MB`
                  : `${Math.max(1, Math.round(p.size / 1024))} KB`}
              </span>
              <span
                className="inline-flex items-center gap-1 h-6 px-2 text-xs font-medium rounded-full border bg-zinc-50 text-zinc-500 border-zinc-200"
              >
                <Loader2 size={11} className="animate-spin" />
                Uploading…
              </span>
            </div>
          </div>
        ))}
        {docs.map((doc) => {
          const inFlight = (
            doc.status === ('uploading' as any)   // RQ async path pre-INSERT
            || doc.status === ('uploaded' as any)
            || doc.status === ('extracting' as any)
            || doc.status === ('indexing' as any)
            || doc.status === ('processing' as any)
          );
          const failed = doc.status === 'failed';
          const indexed = doc.status === 'indexed';
          const stale = !!doc.stale && inFlight;

          const armed = confirmDeleteDocId === doc.id;
          const deleting = deletingDocId === doc.id;
          const retrying = retryingId === doc.id;

          // Per-file extras (warnings + char count) live on the
          // legacy idf_document table, joined by filename. When the
          // join misses (supplementary / prior-art rows) we just
          // skip those columns.
          const legacy = legacyDocsByName?.get(doc.doc_name);
          const warns = (legacy?.extraction_warnings || []) as string[];
          const visionUsed = warns.some((w) =>
            typeof w === 'string' && w.toLowerCase().includes('vision extraction enriched'),
          );
          const userWarns = warns.filter((w) =>
            typeof w === 'string' && !w.toLowerCase().includes('vision extraction enriched'),
          );
          const hasWarnings = userWarns.length > 0;
          const charCount = (legacy?.extracted_text || '').length;

          // Per-format icon + colour. SC2 + SC4 ingestion
          // extension added new formats (markdown / html / rtf
          // / csv / tsv / json / xml / eml); each gets a
          // dedicated icon so the row reads "what kind of file
          // is this" at a glance without parsing the filename.
          // Fallback for unknown formats is FileText/blue.
          const formatKey = (doc.file_format || '').toLowerCase();
          const { FormatIcon, formatIconClass } = (() => {
            switch (formatKey) {
              case 'pdf':
                return { FormatIcon: File, formatIconClass: 'text-red-500' };
              case 'csv':
              case 'tsv':
              case 'xlsx':
              case 'xlsm':
                return { FormatIcon: FileSpreadsheet, formatIconClass: 'text-emerald-600' };
              case 'json':
              case 'xml':
              case 'html':
              case 'markdown':
                return { FormatIcon: FileCode, formatIconClass: 'text-violet-600' };
              case 'eml':
                return { FormatIcon: Mail, formatIconClass: 'text-amber-600' };
              case 'rtf':
                return { FormatIcon: FileText, formatIconClass: 'text-zinc-600' };
              case 'png':
              case 'jpg':
              case 'jpeg':
              case 'gif':
              case 'tif':
              case 'tiff':
              case 'bmp':
              case 'webp':
                return { FormatIcon: FileImage, formatIconClass: 'text-orange-500' };
              default:
                return { FormatIcon: FileText, formatIconClass: 'text-blue-500' };
            }
          })();

          // Status chip styling. Each status maps to a fixed colour:
          //   indexed (clean)   → green
          //   indexed (warns)   → amber
          //   processing/inflight → blue + pulse dot
          //   stuck             → amber (escalated)
          //   failed            → red
          //   uploading/other   → zinc (neutral) + spinner
          let statusLabel: string;
          let statusClass: string;
          let StatusIcon: React.ComponentType<any>;
          if (failed) {
            statusLabel = 'Failed';
            statusClass = 'bg-red-50 text-red-700 border-red-200';
            StatusIcon = AlertTriangle;
          } else if (stale) {
            statusLabel = 'Stuck';
            statusClass = 'bg-amber-50 text-amber-800 border-amber-300';
            StatusIcon = AlertTriangle;
          } else if (inFlight) {
            statusLabel = 'Processing…';
            statusClass = 'bg-blue-50 text-blue-700 border-blue-200';
            StatusIcon = Loader2;
          } else if (indexed && hasWarnings) {
            statusLabel = 'Indexed (warnings)';
            statusClass = 'bg-amber-50 text-amber-700 border-amber-200';
            StatusIcon = AlertTriangle;
          } else if (indexed) {
            statusLabel = 'Indexed';
            statusClass = 'bg-green-50 text-green-700 border-green-200';
            StatusIcon = CheckCircle;
          } else {
            statusLabel = String(doc.status);
            statusClass = 'bg-zinc-50 text-zinc-600 border-zinc-200';
            StatusIcon = FileText;
          }

          // Type-pill render mode for the row. Indexed non-archived
          // rows get the clickable DocTypePicker; in-flight / failed
          // rows show a static pill (the type isn't drafter-editable
          // until the pipeline settles); archived rows show a static
          // greyed "Archived" badge with no dropdown.
          const dt = (doc.doc_type || '').toLowerCase();
          const isArchivedIdf = dt === 'idf_archived';
          const typeEditable = doc.status === 'indexed' && !isArchivedIdf;
          const pickerValue: DocTypeValue =
            (dt === 'idf' || dt === 'supplementary' || dt === 'prior_art')
              ? dt as DocTypeValue
              : 'supplementary';

          // Authoritative char count: prefer matter_documents.char_count
          // (server-side) over the legacy idf_documents join. Either
          // can be missing on a fresh row whose extraction is still
          // in flight, in which case no badge renders.
          const charsKnown =
            (typeof doc.char_count === 'number' && doc.char_count > 0)
              ? doc.char_count
              : (charCount > 0 ? charCount : 0);

          return (
            <div
              key={doc.id}
              data-testid={`uploaded-file-${doc.id}`}
              data-status={doc.status}
              data-stale={stale ? 'true' : 'false'}
              className={
                'px-4 py-3 ' + (isArchivedIdf ? 'opacity-50' : '')
              }
            >
              {/* Row 1 — file icon, name, type pill, status, actions */}
              <div className="flex items-center gap-2.5 text-sm">
                <FormatIcon
                  size={16}
                  className={`${formatIconClass} flex-shrink-0`}
                />
                <span
                  className={
                    'truncate flex-1 max-w-[24ch] '
                    + (isArchivedIdf
                      ? 'text-zinc-500 italic'
                      : 'text-zinc-800')
                  }
                  title={doc.doc_name}
                >
                  {doc.doc_name}
                </span>

                {/* Doc-type indicator */}
                {isArchivedIdf ? (
                  <span
                    data-testid={`doc-type-archived-${doc.id}`}
                    className="inline-flex items-center h-6 px-2 text-xs font-medium rounded-full border bg-zinc-100 text-zinc-500 border-zinc-200 flex-shrink-0"
                    title="A newer IDF replaced this one. Restore via the API to bring it back."
                  >
                    Archived
                  </span>
                ) : !typeEditable ? (
                  <span
                    data-testid={`doc-type-pill-${doc.id}`}
                    className={
                      'inline-flex items-center h-6 px-2 text-xs '
                      + 'font-medium rounded-full border flex-shrink-0 '
                      + docTypePillClasses(pickerValue)
                    }
                  >
                    {docTypeLabel(pickerValue)}
                  </span>
                ) : (
                  <DocTypePicker
                    docId={doc.id}
                    value={pickerValue}
                    disabled={updatingTypeId === doc.id || !!isFrozen}
                    isFrozen={!!isFrozen}
                    onChange={(next) => handleDocTypeChange(doc, next)}
                  />
                )}

                {/* Status badge — consistent h-6, rounded-full, with a
                    pulsing dot for active in-flight rows so the eye
                    catches "this one is moving" without scanning the
                    whole list. */}
                <span
                  data-testid={`uploaded-file-status-${doc.id}`}
                  className={
                    'inline-flex items-center gap-1.5 h-6 px-2 '
                    + 'text-xs font-medium rounded-full border '
                    + 'flex-shrink-0 ' + statusClass
                  }
                  title={stale
                    ? `In ${doc.status} status since ${doc.uploaded_at}; backend flagged as stuck (>5 min)`
                    : statusLabel}
                >
                  {inFlight && !stale ? (
                    <span className="inline-block w-1.5 h-1.5 rounded-full bg-current opacity-70 animate-pulse" />
                  ) : (
                    <StatusIcon
                      size={11}
                      className={inFlight && !stale ? 'animate-spin' : ''}
                    />
                  )}
                  {statusLabel}
                </span>

                {/* Action buttons — only on editable, non-frozen rows */}
                {typeEditable && (failed || stale) && !isFrozen && (
                  <button
                    type="button"
                    data-testid={`uploaded-file-retry-${doc.id}`}
                    onClick={() => handleRetry(doc.id)}
                    disabled={retrying}
                    title="Re-run the indexing pipeline for this document"
                    className="text-xs text-[#002FA7] hover:underline disabled:opacity-50 flex-shrink-0"
                  >
                    {retrying ? 'Retrying…' : 'Retry'}
                  </button>
                )}
                {typeEditable && !isFrozen && doc.id > 0 && (
                  <button
                    type="button"
                    data-testid={`delete-document-${doc.id}`}
                    onClick={() => handleDelete(doc.id)}
                    onBlur={() => setConfirmDeleteDocId((cur) => (cur === doc.id ? null : cur))}
                    disabled={deleting}
                    title={armed ? 'Click again to confirm' : 'Delete this document'}
                    className={
                      'p-1.5 rounded-md text-xs transition-colors flex items-center gap-1 flex-shrink-0 '
                      + (armed
                        ? 'bg-red-600 text-white hover:bg-red-700'
                        : 'text-zinc-400 hover:text-red-600 hover:bg-red-50')
                    }
                  >
                    {deleting
                      ? <Loader2 size={12} className="animate-spin" />
                      : <Trash2 size={12} />}
                    {armed && !deleting && <span className="text-[10px] pr-1">Confirm</span>}
                  </button>
                )}
              </div>

              {/* Row 2 — secondary signals: char count, vision badge,
                  classifier auto tag. Indented under the filename so
                  the eye reads them as belonging to the row above. */}
              {(charsKnown > 0 || visionUsed || doc.classifier_label) && (
                <div className="mt-1.5 pl-[26px] flex items-center gap-2 flex-wrap">
                  {charsKnown > 0 && (
                    <span
                      data-testid={`uploaded-file-charcount-${doc.id}`}
                      className={
                        'inline-flex items-center h-5 px-1.5 text-[11px] '
                        + 'font-mono rounded border '
                        + charCountBadgeCls(charsKnown)
                      }
                      title={
                        `${charsKnown.toLocaleString()} characters extracted `
                        + `(~${Math.round(charsKnown / 4).toLocaleString()} tokens)`
                        + (charsKnown > 100_000
                          ? ' — exceeds 100k; content beyond ~80k chars will not be indexed.'
                          : charsKnown > 80_000
                            ? ' — near the 80k recommended limit.'
                            : '')
                      }
                    >
                      {charsKnown > 100_000
                        ? '⚠ '
                        : charsKnown > 80_000
                          ? '⚠ '
                          : ''}
                      {formatCharCount(charsKnown)}
                    </span>
                  )}
                  {visionUsed && (
                    <span
                      className="inline-flex items-center h-5 px-1.5 text-[11px] rounded border bg-purple-50 text-purple-700 border-purple-200"
                      title="The images in this document were described by the AI"
                    >
                      images processed
                    </span>
                  )}
                  {doc.classifier_label && (
                    <span
                      data-testid={`doc-classifier-auto-${doc.id}`}
                      className="text-[11px] text-zinc-500 italic"
                      title={`Auto-classified as ${doc.classifier_label.replace(/_/g, ' ')}`}
                    >
                      · auto
                    </span>
                  )}
                </div>
              )}

              {/* Warnings — stacked amber sub-rows under the filename.
                  Indentation matches the secondary signals row so
                  they read as a single hierarchy. */}
              {hasWarnings && (
                <div className="mt-1.5 pl-[26px] space-y-0.5">
                  {userWarns.slice(0, 3).map((w, idx) => (
                    <div
                      key={idx}
                      className="text-xs text-amber-700 flex items-start gap-1.5"
                    >
                      <AlertTriangle
                        size={11}
                        className="flex-shrink-0 mt-0.5 text-amber-500"
                      />
                      <span>{w}</span>
                    </div>
                  ))}
                </div>
              )}

              {/* Classifier-conflict promote prompt. Backend sets
                  classifier_conflict when the lite-tier classifier
                  called this doc an IDF but a primary already
                  exists; row stays as supplementary and the drafter
                  sees a one-click Promote affordance. Promote
                  routes through handleDocTypeChange so the existing
                  confirm() dialog + server-side demotion of the
                  prior primary fire. */}
              {doc.classifier_conflict
                && doc.status === 'indexed'
                && !isFrozen && (
                <div
                  data-testid={`classifier-conflict-${doc.id}`}
                  className="mt-1.5 pl-[26px] flex items-center gap-2 text-xs text-amber-700"
                >
                  <AlertTriangle size={11} className="flex-shrink-0 text-amber-500" />
                  <span>Looks like an IDF — make this primary?</span>
                  <button
                    type="button"
                    data-testid={`classifier-conflict-promote-${doc.id}`}
                    onClick={() => handleDocTypeChange(doc, 'idf')}
                    disabled={updatingTypeId === doc.id}
                    className="underline hover:text-amber-900 disabled:opacity-50"
                  >
                    Promote
                  </button>
                </div>
              )}

              {/* Per-row step-progress panel — only when the row is
                  in-flight AND we know the job_id (async-path
                  uploads). Sync-path uploads never populate the
                  map so this section never renders for them; with
                  use_async_upload=false the entire branch is
                  inert (jobIdsByDocument stays empty). */}
              {inFlight && jobIdsByDocument?.[String(doc.id)] && (
                <JobProgressPanel
                  jobId={jobIdsByDocument[String(doc.id)]}
                  onSettled={() => {
                    // Kick the listMatterDocuments poll immediately
                    // so the row's chip flips from 'uploading'
                    // (or 'processing') to 'indexed' / 'failed'
                    // without waiting for the next 5s tick.
                    fetchDocuments();
                  }}
                />
              )}
            </div>
          );
        })}
      </div>

      {/* OPT-5 — "Updated Xs ago" label. Only renders when at
          least one real row is on screen (placeholder-only
          panels skip it: nothing has been fetched yet). The
          counter ticks every second; the lastRefresh stamp is
          set at the end of every successful fetchDocuments(). */}
      {docs.length > 0 && (
        <p
          data-testid="uploaded-files-last-refresh"
          className="text-xs italic text-zinc-500 px-4 py-2 border-t border-zinc-100 text-right"
        >
          Updated {secondsAgo}s ago
        </p>
      )}
    </div>
  );
}
